/**
 * <tab-truncate> — a collapsible tab's content shortened on phones
 * (snippets/product-collapsible-tab.liquid, "Shorten on phones"). The clamp
 * itself is CSS (section-main-product.css, phone widths only); this only
 * wires the Read more / Read less toggle and removes it when the content
 * already fits, so short descriptions never show a pointless link.
 */
if (!customElements.get('tab-truncate')) {
  class TabTruncate extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('.tab-truncate__body');
      this.toggle = this.querySelector('.tab-truncate__toggle');
      if (!this.body || !this.toggle) return;

      this.label = this.toggle.querySelector('.tab-truncate__label') || this.toggle;
      this.toggle.addEventListener('click', () => this.setExpanded(!this.classList.contains('is-expanded')));
      // Tapping the shortened text itself opens it too; links inside keep working.
      this.body.addEventListener('click', (event) => {
        if (this.classList.contains('is-expanded') || this.classList.contains('is-short')) return;
        if (event.target.closest('a, button')) return;
        this.setExpanded(true);
      });

      // Measure once the tab is visible (a closed <details> gives zero
      // heights) and again if the viewport changes.
      this.measure = this.measure.bind(this);
      this.measure();
      const details = this.closest('details');
      if (details) details.addEventListener('toggle', this.measure);
      window.addEventListener('resize', this.measure, { passive: true });
      // Content can grow after the first measure: images without a set
      // height, web fonts swapping in. Re-measure as they land.
      this.body.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', this.measure, { once: true });
      });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measure);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.measure);
    }

    measure() {
      if (this.classList.contains('is-expanded')) return;
      // Only the phone stylesheet clamps; elsewhere there is nothing to toggle.
      const clamped = window.getComputedStyle(this.body).overflowY === 'hidden';
      const overflows = clamped && this.body.scrollHeight > this.body.clientHeight + 4;
      this.classList.toggle('is-short', !overflows);
    }

    setExpanded(expanded) {
      this.classList.toggle('is-expanded', expanded);
      this.toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.label.textContent = expanded ? this.toggle.dataset.less : this.toggle.dataset.more;
      if (!expanded) {
        // Collapsing can leave the viewport below the tab; bring it back.
        const tab = this.closest('details') || this;
        if (tab.getBoundingClientRect().top < 0) tab.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  }
  customElements.define('tab-truncate', TabTruncate);
}
